/**
 * @file 子账号校区概览
 * @description 统计卡 + 欢迎语；子账号进入校区管理时的首页形态。
 *
 * @whenToUse 校区管理页 · 子账号权限视图。
 * @whenNotToUse 主账号列表视图。
 */

import type { ReactNode } from 'react'
import { Coins, GraduationCap, Users } from 'lucide-react'
import { cn } from '@/lib/cn.ts'
import type { CampusRow } from '@/components/pages/campus-page/mock.ts'

export type CampusSubOverviewProps = {
  /** 当前子账号所属校区 */
  campus: CampusRow
  /** 展示用昵称；默认演示名 */
  displayName?: string
}

type StatTone = 'primary' | 'success' | 'warning'

type StatItem = {
  key: string
  label: string
  value: number
  unit: string
  tone: StatTone
  icon: ReactNode
}

const toneClass: Record<StatTone, string> = {
  primary: 'bg-primary/15 text-primary',
  success: 'bg-success/15 text-success',
  warning: 'bg-warning/15 text-warning',
}

const WEEKDAY_CN = [
  '星期日',
  '星期一',
  '星期二',
  '星期三',
  '星期四',
  '星期五',
  '星期六',
] as const

/**
 * 按时段返回问候语
 * @param date 当前时间
 */
function greetingByHour(date: Date): string {
  const hour = date.getHours()
  if (hour < 6) return '凌晨好'
  if (hour < 12) return '早上好'
  if (hour < 14) return '中午好'
  if (hour < 18) return '下午好'
  return '晚上好'
}

/**
 * 格式化为「2026 年 8 月 17 日 星期一」
 * @param date 日期
 */
function formatWelcomeDate(date: Date): string {
  return `${date.getFullYear()} 年 ${date.getMonth() + 1} 月 ${date.getDate()} 日 ${WEEKDAY_CN[date.getDay()]}`
}

/**
 * 子账号视角：统计卡 + 欢迎区
 */
export function CampusSubOverview({
  campus,
  displayName = '疯狂的狮子Li',
}: CampusSubOverviewProps) {
  const now = new Date()
  const stats: StatItem[] = [
    {
      key: 'teachers',
      label: '教师数量',
      value: campus.teacherCount,
      unit: '人',
      tone: 'primary',
      icon: <GraduationCap className="size-5" aria-hidden />,
    },
    {
      key: 'students',
      label: '学员数量',
      value: campus.studentCount,
      unit: '人',
      tone: 'success',
      icon: <Users className="size-5" aria-hidden />,
    },
    {
      key: 'points',
      label: '剩余点数',
      value: campus.points,
      unit: '点',
      tone: 'warning',
      icon: <Coins className="size-5" aria-hidden />,
    },
  ]

  return (
    <div className="space-y-6">
      <div className="space-y-4">
        <p className="text-sm text-muted">
          当前校区「{campus.name}」· 主账号 {campus.account}
        </p>
        <div className="grid gap-4 sm:grid-cols-3">
          {stats.map((stat) => (
            <article
              key={stat.key}
              className="rounded-2xl border border-border bg-raised p-5 transition-opacity duration-200 hover:opacity-90"
            >
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="text-sm text-muted">{stat.label}</p>
                  <p className="mt-2 flex items-baseline gap-1">
                    <span className="text-3xl font-semibold tabular-nums tracking-tight text-foreground">
                      {stat.value}
                    </span>
                    <span className="text-sm text-muted">{stat.unit}</span>
                  </p>
                </div>
                <div
                  className={cn(
                    'inline-flex size-11 shrink-0 items-center justify-center rounded-xl',
                    toneClass[stat.tone],
                  )}
                >
                  {stat.icon}
                </div>
              </div>
            </article>
          ))}
        </div>
      </div>

      <section className="rounded-2xl border border-border bg-raised p-6">
        <h2 className="text-xl font-semibold tracking-tight text-foreground">
          {greetingByHour(now)}，{displayName}
        </h2>
        <p className="mt-4 text-base text-foreground">
          欢迎使用{' '}
          <span className="font-medium text-primary">思犇优学</span>{' '}
          管理系统
        </p>
        <p className="mt-2 text-sm text-muted">
          请从左侧菜单进入对应功能模块，开始今天的管理工作。
        </p>
        <p className="mt-6 text-sm tabular-nums text-muted">
          {formatWelcomeDate(now)}
        </p>
      </section>
    </div>
  )
}
